'use client';

import { DefaultChatTransport } from 'ai';
import { useCallback, useMemo } from 'react';
import { useChat } from '@ai-sdk/react';
import env from '@beam-australia/react-env';
import { AGENT_ENDPOINTS } from '@beamible/ai/agent/config/endpoints';
import { useAccessToken } from '@pkg/auth/AccessTokenProvider';

const { path, id: defaultId } = AGENT_ENDPOINTS.tooltip;

/**
 * Client hook for TooltipAgent
 *
 * @param {Object} options
 * @param {Function} options.onFinish - Callback when completion finishes
 * @param {Function} options.onError - Callback on error
 * @param {string} options.id - Chat session ID
 *
 * @example
 * const { completion, complete, isLoading, error } = useTooltipAgent();
 * await complete('Analyze this data: ...');
 */
export function useTooltipAgent(options = {}) {
  const { onFinish, onError, id = defaultId } = options;
  const { accessToken } = useAccessToken();

  const transport = new DefaultChatTransport({
    api: `${env('API_HOST')}${path}`,
    headers: () => ({
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    }),
  });

  const { messages, sendMessage, status, error, stop } = useChat({
    transport,
    id,
    onFinish,
    onError,
  });

  const isLoading = status === 'streaming' || status === 'submitted';

  // Extract completion from messages (v6 parts format is canonical)
  const completion = useMemo(() => {
    const lastAssistant = messages.filter((m) => m.role === 'assistant').at(-1);
    if (!lastAssistant) return '';

    // v6: parts is the canonical format
    if (lastAssistant.parts?.length) {
      return lastAssistant.parts
        .filter((p) => p.type === 'text')
        .map((p) => p.text)
        .join('\n\n');
    }

    // Fallback for older/custom endpoints
    return lastAssistant.content || '';
  }, [messages]);

  const complete = useCallback(
    async (prompt) => {
      await sendMessage({
        role: 'user',
        content: prompt,
      });
    },
    [sendMessage]
  );

  return {
    completion,
    complete,
    isLoading,
    error,
    stop,
    messages,
    status,
  };
}
